import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePlan } from '../context/PlanContext';
import { PlanGate, UsageMeter } from '../components/PlanGate';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardSidebar from '../components/DashboardSidebar';
import NotificationBell from '../components/NotificationBell';
import { useWebSocket } from '../hooks/useWebSocket';
import { usePendingSharesNotifier } from '../hooks/usePendingSharesNotifier';
import { useIncomingCallNotifier } from '../hooks/useIncomingCallNotifier';
import IncomingCallOverlay from '../components/IncomingCallOverlay';
import { TosModal } from '../components/TosModal';
import DailyTracker from '../components/DailyTracker';

import CommunicationHub from './CommunicationHub';
import Journal from './Journal';
import CalendarPage from './CalendarPage';
import PlanBuilder from './PlanBuilder';
import Billing from './Billing';
import Profile from './Profile';
import PlanAnalysis from './PlanAnalysis';
import ProgressHub from './ProgressHub';
import PrivacyCenter from './PrivacyCenter';
import Courses from './Courses';
import AdminPanel from './AdminPanel';
import CoParentLink from './CoParentLink';
import Insights from './Insights';
import {
  MessageSquare,
  FileText,
  BookOpen,
  Calendar,
  Menu,
  BarChart3,
  Award,
  Shield,
  Trophy
} from 'lucide-react';

import { useSyncRefresh } from '../hooks/useSyncRefresh';

import API from '../utils/api';

const DashboardHome = ({ user, stats, setActivePage }) => {
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState({ communication: [], journal: [] });
  
  useEffect(function() {
    axios.get(API + '/dashboard/timeline', { withCredentials: true })
      .then(function(res) { setTimeline(res.data); })
      .catch(function() {});
  }, []);

  useSyncRefresh(useCallback(function() {
    axios.get(API + '/dashboard/timeline', { withCredentials: true })
      .then(function(res) { setTimeline(res.data); })
      .catch(function() {});
  }, []));

  const quickActions = [
    { title: 'Analyze a Message', desc: 'Run tone & risk analysis', icon: MessageSquare, id: 'communication' },
    { title: 'Add Journal Entry',  desc: 'Document an event objectively', icon: FileText,       id: 'journal' },
    { title: 'Build Parenting Plan', desc: 'Resume the 14-stage builder', icon: BookOpen,       id: 'plan-builder' },
    { title: 'View Calendar',      desc: 'Overnights & exchanges',       icon: Calendar,       id: 'calendar' },
  ];

  const getScoreColor = (score) => {
    const colors = {
      'Red': '#DC2626',
      'Orange': '#EA580C',
      'Yellow': '#CA8A04',
      'Blue': '#2563EB',
      'Green': '#059669'
    };
    return colors[score] || '#2563EB';
  };

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Greeting block — flat, no extraneous chrome. The dashboard surface
          carries the page; the greeting is just typography. */}
      <header className="space-y-1.5">
        <h2 className="font-serif-display text-[28px] leading-[36px] tracking-[-0.022em] text-[color:var(--text-primary)]">
          Welcome back, {user?.username || user?.name || 'User'}
        </h2>
        <p className="text-[15px] leading-[22px] text-[color:var(--text-muted)]">
          Here&rsquo;s your co-parenting overview at a glance.
        </p>
      </header>

      {/* Quick Actions */}
      <section aria-labelledby="quick-actions-title">
        <h3 id="quick-actions-title" className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)] mb-3">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => setActivePage(action.id)}
              className="card-surface card-pad-md text-left motion-lift hover:-translate-y-0.5 group"
              data-testid={`quick-action-${action.id}`}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#EEEFF8] border border-[#DDE1EE] motion-border group-hover:border-[#A8AAD4] mb-4">
                <action.icon className="w-4.5 h-4.5 text-[#2D2560]" strokeWidth={2} />
              </div>
              <h4 className="text-[15px] leading-[20px] font-semibold tracking-[-0.005em] text-[color:var(--text-primary)] mb-1">{action.title}</h4>
              <p className="text-[13px] leading-[18px] text-[color:var(--text-muted)]">{action.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Top-line stats — confident tabular numerals, no chrome competing
          with the data. Each stat is just a label + a number on a clean card. */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <article className="card-surface card-pad-md" data-testid="stat-messages">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-[color:var(--text-muted)]" strokeWidth={2} />
            <h4 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Messages Analyzed</h4>
          </div>
          <p className="tabular text-[34px] leading-[40px] font-bold tracking-[-0.024em] text-[color:var(--text-primary)]">{stats?.messages_analyzed || 0}</p>
        </article>

        <article className="card-surface card-pad-md" data-testid="stat-journal">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-[color:var(--text-muted)]" strokeWidth={2} />
            <h4 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Journal Entries</h4>
          </div>
          <p className="tabular text-[34px] leading-[40px] font-bold tracking-[-0.024em] text-[color:var(--text-primary)]">{stats?.journal_entries || 0}</p>
        </article>

        <article className="card-surface card-pad-md" data-testid="stat-events">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-[color:var(--text-muted)]" strokeWidth={2} />
            <h4 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Calendar Events</h4>
          </div>
          <p className="tabular text-[34px] leading-[40px] font-bold tracking-[-0.024em] text-[color:var(--text-primary)]">{stats?.calendar_events || 0}</p>
        </article>
      </section>

      {/* Communication Quality */}
      <section className="card-surface card-pad-md" data-testid="comm-quality-card">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-4 h-4 text-[color:var(--text-muted)]" strokeWidth={2} />
          <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Communication Quality</h3>
        </div>
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-[13px]"
            style={{
              backgroundColor: `${getScoreColor(stats?.communication_quality)}14`,
              color: getScoreColor(stats?.communication_quality),
              border: `1.5px solid ${getScoreColor(stats?.communication_quality)}`
            }}
            data-testid="comm-quality-pill"
          >
            {stats?.communication_quality || 'N/A'}
          </div>
          <div>
            <p className="text-[15px] leading-[22px] font-semibold text-[color:var(--text-primary)]">
              Current Status: {stats?.communication_quality || 'Not analyzed yet'}
            </p>
            <p className="text-[13px] leading-[18px] text-[color:var(--text-muted)] mt-0.5">
              Based on recent message analysis
            </p>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={() => setActivePage('progress')} className="card-surface card-pad-md text-left motion-lift hover:-translate-y-0.5 group" data-testid="progress-card">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-[color:var(--text-muted)]" strokeWidth={2} />
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Your Progress</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-[14px] text-[color:var(--text-secondary)]">Points</span>
              <span className="tabular text-[22px] leading-[28px] font-bold tracking-[-0.015em] text-[color:var(--text-primary)]">{stats?.points || 0}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-[14px] text-[color:var(--text-secondary)]">Badges</span>
              <span className="tabular text-[22px] leading-[28px] font-bold tracking-[-0.015em] text-[color:var(--text-primary)]">{stats?.badges?.length || 0}</span>
            </div>
            {stats?.leaderboard_rank && (
              <div className="flex justify-between items-baseline pt-3 border-t border-[color:var(--border-subtle)]">
                <span className="text-[14px] text-[color:var(--text-secondary)] flex items-center gap-1.5">
                  <Trophy className="w-4 h-4 text-[#CA8A04]" strokeWidth={2} />
                  Leaderboard Rank
                </span>
                <span className="tabular text-[22px] leading-[28px] font-bold tracking-[-0.015em] text-[#CA8A04]">
                  #{stats.leaderboard_rank}
                  <span className="text-[12px] text-[color:var(--text-muted)] font-normal ml-1">/ {stats.total_leaderboard || '?'}</span>
                </span>
              </div>
            )}
          </div>
        </button>

        <div className="card-surface card-pad-md" data-testid="subscription-card">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-[color:var(--text-muted)]" strokeWidth={2} />
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">Subscription</h3>
          </div>
          <div className="space-y-4">
            <div>
              <span className="text-[12px] font-medium text-[color:var(--text-muted)]">Current Plan</span>
              <p className="text-[22px] leading-[28px] font-bold tracking-[-0.015em] capitalize text-[color:var(--text-primary)] mt-0.5">{stats?.subscription_plan || 'Free'}</p>
            </div>
            {stats?.subscription_plan !== 'pro' && (
              <button
                onClick={() => navigate('/billing')}
                className="btn btn-md btn-primary w-full motion-bg"
                data-testid="upgrade-plan-btn"
              >
                Upgrade Plan
              </button>
            )}
          </div>
        </div>
      </section>

      <p className="text-[12px] text-[color:var(--text-muted)] text-center max-w-2xl mx-auto leading-relaxed pt-2">
        Parentum is educational only and does not provide legal advice. No attorney-client relationship is created.
      </p>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [activePage, setActivePage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [wsNotification, setWsNotification] = useState(null);
  const [tosNeeded, setTosNeeded] = useState(false);
  
  // Daily Tracker state
  const [showDailyTracker, setShowDailyTracker] = useState(false);
  const [trackerCalendarData, setTrackerCalendarData] = useState(null);
  const [trackerChecked, setTrackerChecked] = useState(false); // Prevent duplicate checks

  const ws = useWebSocket(user?._id);

  // Surface a one-time toast on Google sign-in/up callback redirect.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const isSignup = params.get('google_signup') === '1';
      const isSignin = params.get('google_signin') === '1';
      if (isSignup || isSignin) {
        // Lazy import to keep above-the-fold deps small.
        import('sonner').then(({ toast }) => {
          toast.success(
            isSignup
              ? 'Welcome to Parentum! Your Google Calendar is being synced.'
              : 'Signed in with Google. Calendar sync up to date.',
            { duration: 5000 }
          );
        });
        // Strip query params without triggering a navigation.
        const url = new URL(window.location.href);
        url.searchParams.delete('google_signup');
        url.searchParams.delete('google_signin');
        window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Check if daily tracker should be shown - runs on mount and when user changes
  // PREVIEW ONLY: Daily Tracker only appears in preview environment
  useEffect(function() {
    if (!user) return;
    
    // Only check when on dashboard home page
    if (activePage !== 'dashboard') return;
    
    // Detect if we're in preview (not production)
    var isPreview = window.location.hostname === 'localhost' || 
                    window.location.hostname.includes('preview') ||
                    window.location.hostname.includes('127.0.0.1') ||
                    process.env.REACT_APP_BACKEND_URL?.includes('webhook-trial-debug');
    
    // Only show tracker in preview environment
    if (!isPreview) return;
    
    var today = new Date().toISOString().split('T')[0];
    var lastShown = localStorage.getItem('dailyTrackerLastShown');
    
    // Show if not shown today and haven't already checked this session
    if (lastShown !== today && !trackerChecked) {
      setTrackerChecked(true); // Mark as checked for this session
      fetchAndShowTracker(today);
    }
  }, [user, activePage]); // Check when user loads or when returning to dashboard
  
  // Reset tracker check when the date changes (new day)
  useEffect(function() {
    var checkDateInterval = setInterval(function() {
      var today = new Date().toISOString().split('T')[0];
      var lastShown = localStorage.getItem('dailyTrackerLastShown');
      
      if (lastShown !== today) {
        setTrackerChecked(false); // Allow tracker to show on new day
      }
    }, 60000); // Check every minute
    
    return function() { clearInterval(checkDateInterval); };
  }, []);

  // Function to fetch calendar and show tracker
  const fetchAndShowTracker = function(dateStr) {
    axios.get(API + '/api/calendar', { withCredentials: true })
      .then(function(res) {
        var allEvents = res.data || [];
        
        // Filter events for today's date
        var todayEvents = allEvents.filter(function(e) {
          var eventDate = e.start_date ? e.start_date.split('T')[0] : null;
          return eventDate === dateStr;
        });
        
        // Determine if today is a school day (Monday-Friday)
        var todayDate = new Date(dateStr);
        var dayOfWeek = todayDate.getDay(); // 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
        var isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
        
        // Parse calendar data for tracker
        var calendarData = {
          overnights: todayEvents.filter(function(e) { 
            return (e.type === 'overnight' || e.category === 'overnight') && (e.assignment === 'me' || e.assignment === 'coparent'); 
          }).map(function(e) { 
            return { person: e.assignment, id: e.id || e._id }; 
          }),
          exchanges: todayEvents.filter(function(e) { 
            return e.type === 'exchange' || e.category === 'exchange' || e.assignment === 'exchange'; 
          }),
          schoolDay: isWeekday, // School day is any weekday (Mon-Fri)
          events: todayEvents.filter(function(e) { 
            return e.type === 'event' || e.category === 'event'; 
          }).map(function(e) {
            return { id: e.id || e._id, title: e.title, time: e.time };
          }),
        };
        
        setTrackerCalendarData(calendarData);
        setShowDailyTracker(true);
        localStorage.setItem('dailyTrackerLastShown', dateStr);
      })
      .catch(function(err) {
        console.error('Failed to fetch calendar for tracker:', err);
        // Show tracker anyway with empty calendar data so users can still log
        var todayDate = new Date(dateStr);
        var dayOfWeek = todayDate.getDay();
        var isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
        
        setTrackerCalendarData({
          overnights: [],
          exchanges: [],
          schoolDay: isWeekday,
          events: [],
        });
        setShowDailyTracker(true);
        localStorage.setItem('dailyTrackerLastShown', dateStr);
      });
  };

  // Function to manually open tracker (can be called from Insights page)
  const openTrackerManually = function() {
    var today = new Date().toISOString().split('T')[0];
    fetchAndShowTracker(today);
  };
  
  // Expose openTrackerManually to window so Insights can call it
  useEffect(function() {
    window.openDailyTracker = openTrackerManually;
    return function() {
      delete window.openDailyTracker;
    };
  }, []);

  // Check if user needs to re-accept ToS
  useEffect(() => {
    axios.get(API + '/tos/status', { withCredentials: true })
      .then(function (r) { if (r.data.needs_acceptance) setTosNeeded(true); })
      .catch(function () {});
  }, []);

  var handleTosReaccept = function (version, marketingConsent) {
    // Only send the field that actually changed (ToS). Privacy + Arbitration default
    // to their own CURRENT_* versions on the backend, so we never accidentally write
    // a non-existent version that would cause the modal to keep reopening.
    axios.post(API + '/tos/accept', { tos_version: version, marketing_consent: marketingConsent || false }, { withCredentials: true })
      .then(function () { setTosNeeded(false); })
      .catch(function () {});
  };

  // Listen for navigate-billing events (from upgrade buttons)
  useEffect(() => {
    var handler = function() { setActivePage('settings'); };
    window.addEventListener('navigate-billing', handler);
    return function() { window.removeEventListener('navigate-billing', handler); };
  }, []);

  // Global pending-invite watcher — polls, fires bulk toasts, and updates state anywhere in the app.
  usePendingSharesNotifier(!!user);

  // Incoming video-call invites — polls every 15s and shows Accept/Decline toasts.
  useIncomingCallNotifier(user && (user._id || user.id));

  // When the user clicks "Accept" on the incoming-call overlay, switch to
  // Comms. The pending join was already saved to sessionStorage by the overlay,
  // so VideoCalls.jsx will pick it up on mount — no fragile event timing.
  useEffect(() => {
    var handler = function () {
      setActivePage('communication');
    };
    window.addEventListener('navigate-comms-video', handler);
    return function () { window.removeEventListener('navigate-comms-video', handler); };
  }, []);

  // "Add transcript to journal" from a completed call → route to Journal tab.
  // The journal prefill is already in sessionStorage; Journal.jsx reads it on mount.
  useEffect(() => {
    var handler = function () { setActivePage('journal'); };
    window.addEventListener('navigate-journal-from-call', handler);
    return function () { window.removeEventListener('navigate-journal-from-call', handler); };
  }, []);

  // When the toast's "Review" action fires, send the user to Calendar > Pending Invites tab.
  useEffect(() => {
    var handler = function () {
      setActivePage('calendar');
      // Give CalendarPage a tick to mount before it listens for the tab-switch event.
      setTimeout(function () {
        window.dispatchEvent(new CustomEvent('set-calendar-tab', { detail: 'invites' }));
      }, 50);
    };
    window.addEventListener('navigate-calendar-invites', handler);
    return function () { window.removeEventListener('navigate-calendar-invites', handler); };
  }, []);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Listen for real-time notifications — show browser notification with click-to-navigate
  useEffect(() => {
    ws.onNotification((n) => {
      setWsNotification(n);
      // Show browser push notification
      if ('Notification' in window && Notification.permission === 'granted') {
        var browserNotif = new Notification(n.title || 'Parentum', {
          body: n.body || '',
          icon: '/parentum-mark.png',
          tag: n.id || Date.now().toString(),
        });
        browserNotif.onclick = function () {
          window.focus();
          if (n.link) setActivePage(n.link);
          browserNotif.close();
        };
      }
    });
  }, [ws]);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div className="min-h-screen flex w-full max-w-full overflow-x-hidden" data-testid="dashboard-page">
      {/* Centered overlay for incoming video calls. Always mounted so it can pop over any page. */}
      <IncomingCallOverlay />
      <DashboardSidebar
        activePage={activePage}
        setActivePage={setActivePage}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      <div className="flex-1 min-w-0 max-w-full overflow-x-hidden">
        <header className="sticky top-0 z-30 bg-white border-b border-[#DDE1EE] px-6 py-4" data-testid="dashboard-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-white/60 hover:text-white"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-bold capitalize">{activePage.replace('-', ' ')}</h1>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <NotificationBell onNavigate={setActivePage} wsNotification={wsNotification} />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold">{user?.username || user?.name}</p>
                <p className="text-xs text-white/60">{user?.email}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                {(user?.username || user?.name || 'U').charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        <main className="p-3 sm:p-6">
          {activePage === 'dashboard' && <DashboardHome user={user} stats={stats} setActivePage={setActivePage} />}
          {activePage === 'communication' && (
            <PlanGate feature="messaging" minPlan="basic" label="Co-Parent Messaging requires Basic+">
              <CommunicationHub ws={ws} />
            </PlanGate>
          )}
          {activePage === 'coparent-link' && (
            <PlanGate feature="coparent_linking" minPlan="basic" label="Co-Parent Linking requires Basic+">
              <CoParentLink />
            </PlanGate>
          )}
          {activePage === 'journal' && (
            <PlanGate minPlan="basic" label="Journal requires Basic+">
              <Journal />
            </PlanGate>
          )}
          {activePage === 'calendar' && (
            <PlanGate minPlan="basic" label="Calendar requires Basic+">
              <CalendarPage />
            </PlanGate>
          )}
          {activePage === 'plan-builder' && (
            <PlanGate minPlan="basic" label="Plan Builder requires Basic+">
              <PlanBuilder />
            </PlanGate>
          )}
          {activePage === 'plan-analysis' && <PlanAnalysis />}
          {activePage === 'progress' && <ProgressHub />}
          {activePage === 'analytics' && <Insights />}
          {activePage === 'courses' && <Courses />}
          {activePage === 'admin' && user?.role === 'admin' && <AdminPanel />}
          {activePage === 'privacy' && <PrivacyCenter />}
          {activePage === 'profile' && <Profile />}
          {activePage === 'settings' && <Billing />}
          {!['dashboard', 'communication', 'coparent-link', 'journal', 'calendar', 'plan-builder', 'plan-analysis', 'progress', 'analytics', 'courses', 'admin', 'privacy', 'profile', 'settings'].includes(activePage) && (
            <div className="card-surface p-8 text-center" data-testid="placeholder-page">
              <h2 className="text-2xl font-bold mb-2">{activePage.replace('-', ' ')} (Coming Soon)</h2>
              <p className="text-white/60">This feature is under development</p>
            </div>
          )}
        </main>
        <footer className="p-2 sm:p-3 border-t border-[#DDE1EE] mt-3" data-testid="dashboard-footer">
          <div className="flex flex-col items-center gap-0.5">
            <img src="/parentum-logo.png" alt="Parentum" className="h-4 w-auto parentum-logo-purple-mid" />
            <p className="text-xs text-[#6B6690] text-center max-w-2xl leading-relaxed">
              Parentum is an educational and organizational platform. It is not a law firm and does not provide legal advice. No attorney-client relationship is created by using this platform.
            </p>
            <p className="text-xs text-[#8C86C4]">&copy; 2026 Parentum. All rights reserved.</p>
          </div>
        </footer>
      </div>
      <TosModal open={tosNeeded} onAccept={handleTosReaccept} mode="reaccept" />
      <DailyTracker 
        isOpen={showDailyTracker} 
        onClose={function() { setShowDailyTracker(false); }} 
        calendarData={trackerCalendarData} 
      />
    </div>
  );
};

export default Dashboard;
